import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import './News.css';

/**
 * News Page Component
 * Displays release notes and updates for Mia Flow
 */
function News() {
  const navigate = useNavigate();
  const [releaseNotes, setReleaseNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    fetchReleaseNotes();
    markReleaseAsViewed();
  }, []);

  const fetchReleaseNotes = async () => {
    try {
      setLoading(true);
      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/news`, {
        headers: getBffHeaders(),
      });
      const result = await response.json();
      
      if (result.success && result.data) {
        setReleaseNotes(result.data.content);
        extractVersions(result.data.content);
      } else {
        setError('Failed to load release notes');
      }
    } catch (err) {
      console.error('Failed to fetch release notes:', err);
      setError('Failed to load release notes');
    } finally {
      setLoading(false);
    }
  };

  const extractVersions = (markdown) => {
    const versionRegex = /## Version ([0-9.]+[^\n]*)/g;
    const matches = [];
    let match;
    
    while ((match = versionRegex.exec(markdown)) !== null) {
      matches.push({
        id: match[1].trim(),
        label: match[1].trim()
      });
    }
    
    setVersions(matches);
    if (matches.length > 0) {
      setActiveVersion(matches[0].id);
    }
  };

  const scrollToVersion = (versionId) => {
    setActiveVersion(versionId);
    const headings = document.querySelectorAll('.markdown-content h2');
    for (let heading of headings) {
      if (heading.textContent.includes(versionId)) {
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      }
    }
  };

  const getFilteredContent = () => {
    if (!searchQuery.trim()) {
      return releaseNotes;
    }
    
    const lines = releaseNotes.split('\n');
    const searchLower = searchQuery.toLowerCase();
    const filtered = [];
    
    // Search function using word boundaries
    const matchesSearch = (text) => {
      const words = searchLower.split(/\s+/).filter(w => w.length > 0);
      return words.every(word => {
        const pattern = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
        return pattern.test(text);
      });
    };
    
    // Parse document into hierarchical structure
    let currentVersion = null;
    let currentH3 = null;
    let currentH4 = null;
    let currentH5 = null;
    let sections = [];
    
    for (let line of lines) {
      if (line.startsWith('## Version')) {
        currentVersion = { heading: line, h3Sections: [], content: [] };
        sections.push(currentVersion);
        currentH3 = null;
        currentH4 = null;
        currentH5 = null;
      } else if (line.startsWith('### ') && currentVersion) {
        currentH3 = { heading: line, h4Sections: [], content: [] };
        currentVersion.h3Sections.push(currentH3);
        currentH4 = null;
        currentH5 = null;
      } else if (line.startsWith('#### ') && currentH3) {
        currentH4 = { heading: line, h5Sections: [], content: [] };
        currentH3.h4Sections.push(currentH4);
        currentH5 = null;
      } else if (line.startsWith('##### ') && currentH4) {
        currentH5 = { heading: line, content: [] };
        currentH4.h5Sections.push(currentH5);
      } else {
        // Regular content
        if (currentH5) {
          currentH5.content.push(line);
        } else if (currentH4) {
          currentH4.content.push(line);
        } else if (currentH3) {
          currentH3.content.push(line);
        } else if (currentVersion) {
          currentVersion.content.push(line);
        }
      }
    }
    
    // Filter sections hierarchically
    for (let version of sections) {
      let versionHasMatch = matchesSearch(version.heading + ' ' + version.content.join('\n'));
      const versionLines = [];
      
      for (let h3 of version.h3Sections) {
        let h3HasMatch = matchesSearch(h3.heading + ' ' + h3.content.join('\n'));
        const h3Lines = [];
        
        // Check each H4 subsection
        for (let h4 of h3.h4Sections) {
          let h4HasMatch = matchesSearch(h4.heading + ' ' + h4.content.join('\n'));
          const h4Lines = [];
          
          // Check each H5 subsection
          for (let h5 of h4.h5Sections) {
            if (matchesSearch(h5.heading + ' ' + h5.content.join('\n'))) {
              h4HasMatch = true;
              h4Lines.push(h5.heading);
              h4Lines.push(...h5.content);
            }
          }
          
          // If H4 matches or has matching H5, include it
          if (h4HasMatch) {
            h3HasMatch = true;
            h3Lines.push(h4.heading);
            if (h4Lines.length > 0) {
              h3Lines.push(...h4Lines);
            } else {
              h3Lines.push(...h4.content);
              h4.h5Sections.forEach(h5 => {
                h3Lines.push(h5.heading);
                h3Lines.push(...h5.content);
              });
            }
          }
        }
        
        // If H3 matches or has matching H4, include it
        if (h3HasMatch) {
          versionHasMatch = true;
          versionLines.push(h3.heading);
          if (h3Lines.length > 0) {
            versionLines.push(...h3Lines);
          } else {
            versionLines.push(...h3.content);
            h3.h4Sections.forEach(h4 => {
              versionLines.push(h4.heading);
              versionLines.push(...h4.content);
              h4.h5Sections.forEach(h5 => {
                versionLines.push(h5.heading);
                versionLines.push(...h5.content);
              });
            });
          }
        }
      }
      
      // If version has any match, include it
      if (versionHasMatch) {
        filtered.push(version.heading);
        filtered.push(...version.content);
        filtered.push(...versionLines);
      }
    }
    
    return filtered.length > 0 ? filtered.join('\n') : '# No results found\n\nTry a different search term.';
  };

  const markReleaseAsViewed = async () => {
    try {
      // Fetch current version information
      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/versions`, {
        headers: getBffHeaders(),
      });
      const result = await response.json();
      
      if (result.success && result.data.lastUpdated) {
        // Store the timestamp of the last viewed release
        localStorage.setItem('lastViewedRelease', result.data.lastUpdated);
        
        // Dispatch custom event to notify other components (like NewsButton)
        window.dispatchEvent(new CustomEvent('releaseViewed', { 
          detail: { timestamp: result.data.lastUpdated } 
        }));
      }
    } catch (error) {
      console.error('Failed to mark release as viewed:', error);
    }
  };

  // Simple markdown parser for basic formatting
  const parseMarkdown = (markdown) => {
    if (!markdown) return '';
    
    let html = markdown;
    
    // Headers (order matters: process from most specific to least)
    html = html.replace(/^##### (.*$)/gim, '<h5>$1</h5>');
    html = html.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
    
    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Italic
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Code blocks
    html = html.replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>');
    
    // Inline code
    html = html.replace(/`(.*?)`/g, '<code>$1</code>');
    
    // Images (must come before links)
    html = html.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="markdown-image" />');
    
    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Unordered lists
    html = html.replace(/^\- (.*$)/gim, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
    
    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br/>');
    
    // Wrap in paragraph tags
    html = '<p>' + html + '</p>';
    
    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p><br\/><\/p>/g, '');
    
    return html;
  };

  if (loading) {
    return (
      <div className="news-page">
        <div className="news-hero">
          <div className="news-hero-content">
            <div className="news-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1 className="news-title">What's New in Mia Flow</h1>
            <p className="news-subtitle">Stay up to date with the latest releases and features</p>
          </div>
        </div>
        <div className="news-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading release notes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-page">
        <div className="news-hero">
          <div className="news-hero-content">
            <div className="news-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1 className="news-title">What's New in Mia Flow</h1>
            <p className="news-subtitle">Stay up to date with the latest releases and features</p>
          </div>
        </div>
        <div className="news-content">
          <div className="error-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p>{error}</p>
            <button className="retry-button" onClick={fetchReleaseNotes}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="news-page">
      
      
      <div className="news-hero">
        <div className="news-hero-content">
          <div className="news-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="news-title">What's New in Mia Flow</h1>
          <p className="news-subtitle">Stay up to date with the latest releases and features</p>
          
          <div className="search-container">
           
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                className="search-input"
                placeholder="Search release notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="news-content">
        <div className="release-notes-container">
          <div 
            className="markdown-content"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(getFilteredContent()) }}
          />
        </div>
        
        {versions.length > 0 && (
          <nav className="version-navigation">
            <div className="version-nav-header">Versions</div>
            <ul className="version-list">
              {versions.map((version) => (
                <li key={version.id}>
                  <button
                    className={`version-link ${activeVersion === version.id ? 'active' : ''}`}
                    onClick={() => scrollToVersion(version.id)}
                  >
                    {version.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
}

export default News;
